import { useState, useEffect } from 'react';
import { TrendingUp, HelpCircle, Download, AlertCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import ToolPage from '@/components/ToolPage';
import { toolCardClass, toolInputClass, toolLabelClass, toolButtonOutlineClass } from '@/lib/toolStyles';

interface InvestmentType {
  id: string;
  name: string;
  description: string;
  defaultReturn: number;
  riskLevel: 'Low' | 'Medium' | 'High';
  minReturn: number;
  maxReturn: number;
}

interface InvestmentData {
  year: number;
  totalInvested: number;
  portfolioValue: number;
  inflationAdjustedValue: number;
}

const investmentTypes: InvestmentType[] = [
  {
    id: 'conservative',
    name: 'Conservative',
    description: 'Mostly bonds with some stocks (20/80)',
    defaultReturn: 4,
    riskLevel: 'Low',
    minReturn: 2,
    maxReturn: 6
  },
  {
    id: 'balanced',
    name: 'Balanced',
    description: 'Equal mix of stocks and bonds (60/40)',
    defaultReturn: 7,
    riskLevel: 'Medium',
    minReturn: 4,
    maxReturn: 10
  },
  {
    id: 'growth',
    name: 'Growth',
    description: 'Mostly stocks with some bonds (80/20)',
    defaultReturn: 9,
    riskLevel: 'High',
    minReturn: 5,
    maxReturn: 13
  }
];

const contributionFrequencies = [
  { id: 'monthly', name: 'Monthly', multiplier: 12 },
  { id: 'quarterly', name: 'Quarterly', multiplier: 4 },
  { id: 'annually', name: 'Annually', multiplier: 1 }
];

export default function InvestmentCalculator() {
  const [initialAmount, setInitialAmount] = useState<string>('10000');
  const [regularContribution, setRegularContribution] = useState<string>('200');
  const [contributionFrequency, setContributionFrequency] = useState('monthly');
  const [years, setYears] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState<string>('7');
  const [inflationRate, setInflationRate] = useState<string>('2.5');
  const [investmentType, setInvestmentType] = useState('balanced');
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState<string>('');

  const [projectedData, setProjectedData] = useState<InvestmentData[]>([]);
  const [finalAmount, setFinalAmount] = useState(0);
  const [totalContributions, setTotalContributions] = useState(0);
  const [totalReturn, setTotalReturn] = useState(0);

  const selectedInvestmentType = investmentTypes.find(type => type.id === investmentType)!;
  const selectedFrequency = contributionFrequencies.find(freq => freq.id === contributionFrequency)!;

  // Validate inputs and calculate results
  useEffect(() => {
    validateAndCalculate();
  }, [initialAmount, regularContribution, years, expectedReturn, inflationRate, contributionFrequency, investmentType]);

  const validateAndCalculate = () => {
    const initial = Number(initialAmount);
    const contribution = Number(regularContribution);
    const returnRate = Number(expectedReturn);
    const inflation = Number(inflationRate);

    // Validation
    if (initial < 0) {
      setError('Initial investment cannot be negative');
      return;
    }

    if (contribution < 0) {
      setError('Regular contribution cannot be negative');
      return;
    }

    if (returnRate < selectedInvestmentType.minReturn || returnRate > selectedInvestmentType.maxReturn) {
      setError(`Expected return for ${selectedInvestmentType.name} should be between ${selectedInvestmentType.minReturn}% and ${selectedInvestmentType.maxReturn}%`);
      return;
    }

    if (inflation < 0 || inflation > 10) {
      setError('Inflation rate should be between 0% and 10%');
      return;
    }

    setError('');
    calculateProjection(initial, contribution, returnRate, inflation);
  };

  const calculateProjection = (
    initial: number,
    contribution: number,
    returnRate: number,
    inflation: number
  ) => {
    const data: InvestmentData[] = [];
    const monthlyRate = returnRate / 100 / 12;
    const monthlyInflation = inflation / 100 / 12;
    const contributionsPerYear = selectedFrequency.multiplier;
    const contributionAmount = contribution * contributionsPerYear;

    let currentValue = initial;
    let totalInvested = initial;
    
    for (let year = 0; year <= years; year++) {
      const inflationFactor = Math.pow(1 + monthlyInflation, year * 12);
      
      data.push({
        year,
        totalInvested,
        portfolioValue: currentValue,
        inflationAdjustedValue: currentValue / inflationFactor
      });

      // Calculate next year's value
      for (let month = 1; month <= 12; month++) {
        currentValue *= (1 + monthlyRate);
        
        // Add contribution based on frequency
        if (month % (12 / contributionsPerYear) === 0) {
          currentValue += contribution;
          totalInvested += contribution;
        }
      }
    }

    setProjectedData(data);
    setFinalAmount(currentValue);
    setTotalContributions(totalInvested);
    setTotalReturn(currentValue - totalInvested);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadProjection = () => {
    let csv = 'Year,Total Invested,Portfolio Value,Inflation Adjusted Value\n';
    
    projectedData.forEach(row => {
      csv += `${row.year},${row.totalInvested.toFixed(2)},${row.portfolioValue.toFixed(2)},${row.inflationAdjustedValue.toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'investment-projection.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const investmentChartConfig = {
    portfolioValue: { label: 'Portfolio Value', color: 'hsl(var(--chart-1))' },
    totalInvested: { label: 'Total Invested', color: 'hsl(var(--chart-2))' },
    inflationAdjustedValue: { label: 'Inflation Adjusted', color: 'hsl(var(--chart-3))' },
  };

  return (
    <ToolPage
      title="Investment Calculator"
      description="Project your investment growth and returns over time"
      icon={TrendingUp}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className={toolCardClass}>
              <CardHeader>
                <h2 className="text-lg font-semibold">Investment Details</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Investment Strategy</Label>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    {investmentTypes.map((type) => (
                      <Button
                        key={type.id}
                        variant={investmentType === type.id ? 'default' : 'outline'}
                        size="sm"
                        className="h-auto py-3"
                        onClick={() => {
                          setInvestmentType(type.id);
                          setExpectedReturn(type.defaultReturn.toString());
                        }}
                      >
                        {type.name}
                      </Button>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{selectedInvestmentType.description}</p>
                  <p className="text-xs text-muted-foreground">
                    Risk Level: {selectedInvestmentType.riskLevel} | Expected Return: {selectedInvestmentType.minReturn}% - {selectedInvestmentType.maxReturn}%
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="initial">Initial Investment</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                    <Input id="initial" type="number" min={0} value={initialAmount} onChange={(e) => setInitialAmount(e.target.value)} className="pl-8" />
                  </div>
                </div>

                <div>
                  <Label>Regular Contribution</Label>
                  <div className="grid grid-cols-3 gap-4 mt-2 mb-2">
                    {contributionFrequencies.map((freq) => (
                      <Button key={freq.id} variant={contributionFrequency === freq.id ? 'default' : 'outline'} size="sm" className="h-auto py-3" onClick={() => setContributionFrequency(freq.id)}>
                        {freq.name}
                      </Button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                    <Input type="number" min={0} value={regularContribution} onChange={(e) => setRegularContribution(e.target.value)} className="pl-8" />
                  </div>
                </div>

                <div>
                  <Label>Investment Period (Years)</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {[5, 10, 15, 20, 25].map((period) => (
                      <Button key={period} variant={years === period ? 'default' : 'outline'} size="sm" className="h-auto py-3" onClick={() => setYears(period)}>
                        {period}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="return">Expected Annual Return</Label>
                  <div className="relative">
                    <Input id="return" type="number" step={0.1} min={selectedInvestmentType.minReturn} max={selectedInvestmentType.maxReturn} value={expectedReturn} onChange={(e) => setExpectedReturn(e.target.value)} className="pr-8" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="inflation">Expected Inflation Rate</Label>
                  <div className="relative">
                    <Input id="inflation" type="number" step={0.1} min={0} max={10} value={inflationRate} onChange={(e) => setInflationRate(e.target.value)} className="pr-8" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card className={toolCardClass}>
              <CardHeader className="flex flex-row justify-between items-center">
                <h2 className="text-lg font-semibold">Results</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowInfo(!showInfo)}>
                  <HelpCircle className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Final Amount</p>
                    <p className="text-2xl font-bold">{formatCurrency(finalAmount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Invested</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(totalContributions)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Return</p>
                    <p className="text-2xl font-bold text-brand-700">{formatCurrency(totalReturn)}</p>
                  </div>
                </div>
                {showInfo && (
                  <div className="mt-6 p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      The final amount shows your projected portfolio value after {years} years.
                      Total invested includes your initial investment and all contributions.
                      Total return represents your investment gains (before tax and inflation).
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className={toolCardClass}>
            <CardHeader className="flex flex-row justify-between items-center">
              <h2 className="text-lg font-semibold">Investment Growth Projection</h2>
              <Button variant="secondary" size="sm" onClick={downloadProjection} disabled={projectedData.length === 0}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </CardHeader>
            <CardContent>
              <ChartContainer config={investmentChartConfig} className="h-[400px] w-full">
                <LineChart data={projectedData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="year" tickLine={false} axisLine={false} />
                  <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value))} labelFormatter={(label) => `Year ${label}`} />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Line type="monotone" dataKey="portfolioValue" name="Portfolio Value" stroke="var(--color-portfolioValue)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="totalInvested" name="Total Invested" stroke="var(--color-totalInvested)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="inflationAdjustedValue" name="Inflation Adjusted" stroke="var(--color-inflationAdjustedValue)" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                </LineChart>
              </ChartContainer>
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <h3 className="font-medium mb-2">Understanding the Chart</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[hsl(var(--chart-1))]" />
                    <span>Portfolio Value: Your projected investment value over time</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[hsl(var(--chart-2))]" />
                    <span>Total Invested: Cumulative amount of money invested</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[hsl(var(--chart-3))]" />
                    <span>Inflation Adjusted: Real value accounting for inflation</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
      </div>
    </ToolPage>
  );
}